import { Settings, Eye, Edit } from "lucide-react";
import { memo, useEffect, useState } from "react";

import {
  useGetUserPermission,
  useSetUserPermission,
  type UserPageItemDTO,
  type UserPermissionDTO,
} from "@/common/api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Label,
  Separator,
} from "@/common/components";
import { useToast } from "@/common/hooks";
import { getRoleLabel } from "@/common/lib";
import { UserRoleEnum } from "@/common/model";

interface UserPermissionsModalProps {
  user: UserPageItemDTO | null;
  isOpen: boolean;
  onClose: () => void;
}

const PERMISSION_GROUPS = {
  viewing: {
    title: "Заявка на перевозку груза",
    icon: Eye,
    permissions: [
      {
        key: "createApplication",
        label: "Создание заявки",
        roles: [UserRoleEnum.Admin, UserRoleEnum.CargoOwner],
      },
      {
        key: "cancelApplication",
        label: "Отмена действующей заявки через подтверждение модератором",
        roles: [UserRoleEnum.Admin, UserRoleEnum.CargoOwner],
      },
    ],
  },
  creating: {
    title: "Действия",
    icon: Edit,
    permissions: [
      {
        key: "extensionOfAuction",
        label: "Продление сроков сессии по заявке",
        roles: [UserRoleEnum.Admin, UserRoleEnum.CargoOwner],
      },
      {
        key: "viewingWinners",
        label: "Просмотр победителей в заявке по результатам сессии",
        roles: [UserRoleEnum.Admin, UserRoleEnum.CargoOwner, UserRoleEnum.WagonOwner],
      },
    ],
  },
};

function UserPermissionsModalInner({ user, isOpen, onClose }: UserPermissionsModalProps) {
  const [permissions, setPermissions] = useState<Partial<UserPermissionDTO>>({});
  const { toast } = useToast();

  const { mutateAsync: setUserPermissionAsync, isPending: isSaving } = useSetUserPermission();

  const {
    data: userPermissionsResponse,
    isLoading,
    refetch: refetchUserPermissions,
  } = useGetUserPermission(
    { userId: user?.id ?? "" },
    {
      query: {
        enabled: !!user?.id && isOpen,
      },
    },
  );

  useEffect(() => {
    const payload = userPermissionsResponse?.data.payload;
    if (payload) {
      setPermissions({
        createApplication: payload.createApplication,
        extensionOfAuction: payload.extensionOfAuction,
        cancelApplication: payload.cancelApplication,
        viewingWinners: payload.viewingWinners,
      });
    }
  }, [userPermissionsResponse]);

  const handlePermissionChange = (permissionKey: string, checked: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [permissionKey]: checked,
    }));
  };

  const handleSave = () => {
    if (!user?.id) return;

    setUserPermissionAsync({
      data: {
        id: user.id,
        createApplication: !!permissions.createApplication,
        extensionOfAuction: !!permissions.extensionOfAuction,
        cancelApplication: !!permissions.cancelApplication,
        viewingWinners: !!permissions.viewingWinners,
      },
    })
      .then(() => {
        return refetchUserPermissions();
      })
      .then(() => {
        toast({
          title: "Успешно",
          description: "Права пользователя обновлены",
        });
        onClose();
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: error instanceof Error ? error.message : "Не удалось обновить права",
        });
      });
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Настройка прав пользователя
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2 ml-7">
            <p className="text-xl font-bold text-left">{user.username}</p>

            <Badge variant="outline">{getRoleLabel(user.role ?? "")}</Badge>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Загрузка прав пользователя...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => {
              const Icon = group.icon;
              // Если роль пользователя WAGON_OWNER — не показываем группу viewing
              if (groupKey === "viewing" && user.role === UserRoleEnum.WagonOwner) {
                return null;
              }
              return (
                <Card key={groupKey}>
                  <CardHeader>
                    <p className="flex items-center gap-2 text-base font-semibold">
                      <Icon className="h-4 w-4" />
                      {group.title}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 max-w-md">
                      {group.permissions.map((permission) => {
                        const isView = permission.roles.includes(user.role as UserRoleEnum);
                        return (
                          isView && (
                            <div key={permission.key} className="flex items-start space-x-2">
                              <Checkbox
                                id={permission.key}
                                checked={!!permissions[permission.key as keyof UserPermissionDTO]}
                                onCheckedChange={(checked) =>
                                  handlePermissionChange(permission.key, checked as boolean)
                                }
                              />
                              <Label
                                htmlFor={permission.key}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {permission.label}
                              </Label>
                            </div>
                          )
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <Separator />

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>
                Отмена
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Сохранение..." : "Сохранить права"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const UserPermissionsModal = memo(UserPermissionsModalInner);
